import React, { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useCarouselContext } from './carousel';

import css from './carousel.module.scss';

type ContentItemProps = {
    index: number;
    children: ({ isInView }: { isInView: boolean }) => React.ReactNode;
};

/**
 * Wrapper around each scrollable item
 *
 * The main purpose of this element is to keep track of whether it is in view or not.
 * That info is used by enhancedChildren to set tabIndex if outside of view.
 */
export const ContentItem = ({ index, children }: ContentItemProps) => {
    const [inViewRef, isInView] = useInView({
        threshold: 0.8,
        rootMargin: '100% 0% 100% 0%'
    });
    const ref = useRef<HTMLDivElement>();
    const context = useCarouselContext();

    const { updateChildrenMap, totalCount } = context;

    // Set multiple refs
    const setRefs = useCallback(
        (node: HTMLDivElement) => {
            // Ref's from useRef needs to have the node assigned to `current`
            ref.current = node;
            // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
            inViewRef(node);
        },
        [inViewRef]
    );

    useEffect(() => {
        updateChildrenMap(index, {
            ref,
            isInView,
            index
        });
    }, [index, isInView, ref, updateChildrenMap]);

    return (
        <div
            ref={setRefs}
            className={css.contentItem}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${totalCount}`}>
            {children({ isInView })}
        </div>
    );
};
